/**
 * Bounded-value helpers for frontend metric attributes.
 *
 * Every attribute we ship to `frontend-events` has to come from a closed set the
 * code can enumerate (professorx/docs/METRICS.md §3): a tag whose values are file
 * names, URLs with ids, user agents or free text is a bug — it blows up both the
 * Mongo cardinality and the `xc_frontend_event_total` label budget. These four
 * functions are the only sanctioned way to turn an unbounded runtime value (a
 * File, a URL, a byte count) into something safe to emit, so the contract lives
 * in one place and is unit-tested once instead of at every emit site.
 */

/** The five size buckets. `FRONTEND_METRICS_DESIGN.md` §4 is the contract. */
export type SizeBucket = '<1MB' | '1-10MB' | '10-100MB' | '100MB-1GB' | '>1GB';

/** Extension we could not safely bound. Also the `safeExt` fallback. */
export const OTHER_EXT = 'other';

// Decision (2026-09-02): buckets are BINARY (1 MB = 1024*1024 B), matching
// FileUploadService.getChunkSize's tiering so an upload's chunk tier and its
// reported sizeBucket never disagree, and the edges are five values wide enough
// that the funnel stays readable while still separating the phone-photo case
// (<1MB) from the video case (>1GB).
const MB = 1024 * 1024;
const GB = 1024 * MB;

/**
 * Bucket a byte count. Unknown / negative / non-finite sizes fall into `<1MB`
 * rather than adding a sixth `unknown` value: the bucket set is part of the
 * published contract and a File with no readable size is, in practice, empty.
 */
export function sizeBucket(bytes: number): SizeBucket {
    const n = typeof bytes === 'number' && isFinite(bytes) && bytes > 0 ? bytes : 0;
    if (n < MB) return '<1MB';
    if (n < 10 * MB) return '1-10MB';
    if (n < 100 * MB) return '10-100MB';
    if (n < GB) return '100MB-1GB';
    return '>1GB';
}

/** Longest extension we keep verbatim; anything longer is not a real extension. */
const MAX_EXT_LENGTH = 8;

/**
 * The lowercased extension of a file name, or `other`.
 *
 * Returns `other` for: no dot, an empty extension, an extension longer than
 * 8 characters, or one containing anything but ASCII letters/digits. That last
 * rule is what keeps a hostile or unusual file name (`report.тест`,
 * `a.<script>`) from becoming an unbounded attribute value.
 */
export function safeExt(name: string): string {
    if (typeof name !== 'string') return OTHER_EXT;
    const base = name.split(/[\\/]/).pop() || '';
    const dot = base.lastIndexOf('.');
    if (dot < 0 || dot === base.length - 1) return OTHER_EXT;
    const ext = base.slice(dot + 1).toLowerCase();
    if (ext.length === 0 || ext.length > MAX_EXT_LENGTH) return OTHER_EXT;
    if (!/^[a-z0-9]+$/.test(ext)) return OTHER_EXT;
    return ext;
}

/** Hard cap on an `endpoint` attribute; longer templates are truncated. */
const MAX_ENDPOINT_LENGTH = 80;

const HEX24 = /^[0-9a-f]{24}$/i;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC = /^\d+$/;

/**
 * Turn a request URL into a bounded path template: no origin, no query, no
 * fragment, ids replaced by `:id`.
 *
 * Replaced segments: 24-hex Mongo ObjectIds, RFC-4122 UUIDs (workflow /
 * upload uuids — `StringUtils.uuid()` shapes) and all-numeric segments. The
 * result is truncated to 80 characters so one pathological URL cannot dominate
 * a document. Anything unparseable collapses to `/` rather than leaking the raw
 * string.
 */
export function endpointTemplate(url: string): string {
    if (typeof url !== 'string' || url.length === 0) return '/';
    let path = url;
    try {
        // Absolute URL → pathname. Relative path → strip query/hash by hand.
        if (/^[a-z][a-z0-9+.-]*:\/\//i.test(url)) {
            path = new URL(url).pathname;
        } else {
            path = url.split('#')[0].split('?')[0];
        }
    } catch {
        path = url.split('#')[0].split('?')[0];
    }
    if (!path.startsWith('/')) path = '/' + path;
    const templated = path
        .split('/')
        .map(seg => (HEX24.test(seg) || UUID.test(seg) || NUMERIC.test(seg) ? ':id' : seg))
        .join('/');
    const collapsed = templated.length > 1 && templated.endsWith('/')
        ? templated.slice(0, -1)
        : templated;
    return collapsed.length > MAX_ENDPOINT_LENGTH
        ? collapsed.slice(0, MAX_ENDPOINT_LENGTH)
        : collapsed;
}

/**
 * Hostname of a URL — no scheme, no port, no path, no query.
 *
 * This is the `edgeHost` attribute already used by `THUMBNAIL_LOAD_FAILED`: it
 * is bounded by the size of the fleet (~20 edges + pod + api), which is exactly
 * the dimension we need to spot "one edge is failing" without carrying a URL.
 * Returns '' when there is no host to read.
 */
export function hostOnly(url: string): string {
    if (typeof url !== 'string' || url.length === 0) return '';
    try {
        if (/^[a-z][a-z0-9+.-]*:\/\//i.test(url)) {
            return new URL(url).hostname;
        }
        if (typeof window !== 'undefined' && window.location) {
            return new URL(url, window.location.origin).hostname;
        }
    } catch { /* fall through */ }
    return '';
}

/** Closed set for the `errorClass` attribute on FILE_UPLOAD_ERROR / API_ERROR. */
export type MetricErrorClass =
    | 'network'
    | 'timeout'
    | 'http4xx'
    | 'http5xx'
    | 'aborted'
    | 'finalize_missing'
    | 'unknown';

/**
 * Classify a failure into the closed `errorClass` set.
 *
 * `status` is the HTTP status (0 for "never reached the server"). `hint` lets a
 * call site assert a class the status cannot express — `finalize_missing` for
 * the chunk-upload `original-input-files` GET that returns no data (the measured
 * east-32 prod bug, ironman `xc_upload_finalize{outcome="missing"}`), `aborted`
 * for a user-cancelled transfer.
 */
export function errorClass(status: number, hint?: MetricErrorClass, message?: string): MetricErrorClass {
    if (hint) return hint;
    const s = typeof status === 'number' && isFinite(status) ? status : 0;
    if (s >= 400 && s < 500) return 'http4xx';
    if (s >= 500) return 'http5xx';
    const text = typeof message === 'string' ? message.toLowerCase() : '';
    if (text.indexOf('timeout') !== -1 || text.indexOf('timed out') !== -1) return 'timeout';
    if (text.indexOf('abort') !== -1) return 'aborted';
    if (s === 0) return 'network';
    return 'unknown';
}

/**
 * Best-effort HTTP status from any of the error shapes this app throws:
 * `ClientError`/`ServiceError` (`.status`), an rxjs `AjaxError`, an axios error
 * (`.response.status`) or a raw XHR. 0 means "no status" (network / offline).
 */
export function statusOf(error: any): number {
    const candidates = [
        error?.status,
        error?.response?.status,
        error?.xhr?.status,
        error?.originalError?.status,
    ];
    for (const c of candidates) {
        const n = typeof c === 'number' ? c : parseInt(c, 10);
        if (typeof n === 'number' && isFinite(n) && n > 0) return n;
    }
    return 0;
}
